import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { drugService } from '../services/drugService'
import { Drug, DrugSearchFilters } from '../types/drug'
import '../pages/Drugs.css'

export function SettingsDrugs() {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<DrugSearchFilters>({})
  const [selectedDrug, setSelectedDrug] = useState<Drug | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const { data: drugs, isLoading, error } = useQuery({
    queryKey: ['drugs', filters],
    queryFn: () => drugService.getAll(filters),
  })

  // Debug: Log drugs data
  useEffect(() => {
    if (drugs !== undefined) {
      console.log('Drugs loaded:', {
        count: drugs?.length || 0,
        isLoading,
        error: error?.message,
        sample: drugs?.slice(0, 2),
      })
    }
  }, [drugs, isLoading, error])

  const { data: syncStatus } = useQuery({
    queryKey: ['drug-sync-status'],
    queryFn: () => drugService.getSyncStatus(),
    refetchInterval: 5000, // Poll every 5 seconds when syncing
  })

  const syncMutation = useMutation({
    mutationFn: () => drugService.syncFromHPRA(),
    onSuccess: (result) => {
      console.log('Sync successful:', result)
      // Force refetch of drugs
      queryClient.invalidateQueries({ queryKey: ['drugs'] })
      queryClient.invalidateQueries({ queryKey: ['drug-sync-status'] })
      // Also refetch immediately
      queryClient.refetchQueries({ queryKey: ['drugs'] })
    },
    onError: (error) => {
      console.error('Sync mutation error:', error)
    },
  })

  const handleSync = async () => {
    if (window.confirm('This will sync all drugs from HPRA. This may take several minutes. Continue?')) {
      try {
        await syncMutation.mutateAsync()
        alert('Drug sync completed successfully!')
      } catch (error: any) {
        console.error('Sync error:', error)
        alert(`Failed to sync drugs: ${error?.message || 'Unknown error'}`)
      }
    }
  }

  const handleFilterChange = (key: keyof DrugSearchFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setCurrentPage(1) // Reset to first page when filters change
  }

  const handleClearFilters = () => {
    setFilters({})
    setCurrentPage(1)
  }

  const filteredDrugs = useMemo(() => {
    if (!drugs) return []
    return drugs
  }, [drugs])

  // Pagination calculations
  const totalItems = filteredDrugs.length
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0
  const startIndex = totalItems > 0 ? (currentPage - 1) * pageSize : 0
  const endIndex = totalItems > 0 ? Math.min(startIndex + pageSize, totalItems) : 0
  const paginatedDrugs = totalItems > 0 ? filteredDrugs.slice(startIndex, endIndex) : []

  // Reset to page 1 if current page is out of bounds
  if (totalItems > 0 && currentPage > totalPages) {
    setCurrentPage(1)
  } else if (totalItems === 0 && currentPage !== 1) {
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  if (isLoading && !drugs) {
    return (
      <div className="settings-content">
        <div className="loading">Loading drugs...</div>
      </div>
    )
  }

  return (
    <div className="settings-content drugs-page" role="main">
      <div className="drugs-header">
        <div>
          <h1>Drug Management</h1>
          <p className="subtitle">HPRA Drug Database</p>
        </div>
        <div className="header-actions">
          {syncStatus && (
            <div className="sync-status">
              <span className="sync-info">
                Last sync: {syncStatus.lastSyncDate ? new Date(syncStatus.lastSyncDate).toLocaleString() : 'Never'}
              </span>
              <span className="sync-stats">
                {syncStatus.totalProducts || 0} products | {syncStatus.totalWithdrawn || 0} withdrawn |{' '}
                {syncStatus.totalInterchangeable || 0} interchangeable
              </span>
            </div>
          )}
          <button
            className="btn-primary"
            onClick={handleSync}
            disabled={syncMutation.isPending || syncStatus?.syncInProgress}
            aria-label="Sync drugs from HPRA"
          >
            {syncMutation.isPending || syncStatus?.syncInProgress ? 'Syncing...' : 'Sync from HPRA'}
          </button>
        </div>
      </div>

      <div className="drugs-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="search">Search</label>
            <input
              id="search"
              type="text"
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search by name, code, or licence number"
              aria-label="Search drugs"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="productType">Product Type</label>
            <select
              id="productType"
              value={filters.productType || ''}
              onChange={(e) => handleFilterChange('productType', e.target.value || undefined)}
              aria-label="Filter by product type"
            >
              <option value="">All Types</option>
              <option value="HM">Human Medicine (HM)</option>
              <option value="VET">Veterinary</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="marketInfo">Market Status</label>
            <select
              id="marketInfo"
              value={filters.marketInfo || ''}
              onChange={(e) => handleFilterChange('marketInfo', e.target.value || undefined)}
              aria-label="Filter by market status"
            >
              <option value="">All Statuses</option>
              <option value="Marketed">Marketed</option>
              <option value="Not marketed">Not Marketed</option>
              <option value="Unknown">Unknown</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="isWithdrawn">Withdrawn</label>
            <select
              id="isWithdrawn"
              value={filters.isWithdrawn === undefined ? '' : String(filters.isWithdrawn)}
              onChange={(e) =>
                handleFilterChange('isWithdrawn', e.target.value === '' ? undefined : e.target.value === 'true')
              }
              aria-label="Filter by withdrawn status"
            >
              <option value="">All</option>
              <option value="true">Withdrawn</option>
              <option value="false">Active</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="isInterchangeable">Interchangeable</label>
            <select
              id="isInterchangeable"
              value={filters.isInterchangeable === undefined ? '' : String(filters.isInterchangeable)}
              onChange={(e) =>
                handleFilterChange('isInterchangeable', e.target.value === '' ? undefined : e.target.value === 'true')
              }
              aria-label="Filter by interchangeable status"
            >
              <option value="">All</option>
              <option value="true">Interchangeable</option>
              <option value="false">Not Interchangeable</option>
            </select>
          </div>
          <button className="btn-secondary" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {error && (
        <div className="error" role="alert">
          Error loading drugs. Please try again.
        </div>
      )}

      <div className="drugs-content">
        <div className="drugs-list">
          {!isLoading && drugs && drugs.length > 0 && (
            <div className="drugs-header-info">
              <div className="drugs-count">
                Showing {startIndex + 1}-{endIndex} of {totalItems} {totalItems === 1 ? 'drug' : 'drugs'}
              </div>
              <div className="page-size-selector">
                <label htmlFor="pageSize">Items per page:</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  aria-label="Items per page"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          )}
          {!isLoading && (!drugs || drugs.length === 0) ? (
            <div className="empty-state">
              <p>No drugs available. Please sync from HPRA to load drug data.</p>
              <button className="btn-primary" onClick={handleSync} style={{ marginTop: '1rem' }}>
                Sync from HPRA
              </button>
            </div>
          ) : filteredDrugs.length === 0 ? (
            <div className="empty-state">No drugs found matching your filters</div>
          ) : (
            <>
              <div className="drugs-table-container">
                <table className="drugs-table" role="table">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Drug ID</th>
                      <th>Licence Number</th>
                      <th>Dosage Form</th>
                      <th>Market Status</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDrugs.map((drug) => (
                    <tr key={drug.id}>
                      <td>
                        <strong>{drug.productName}</strong>
                        {drug.activeSubstances.length > 0 && (
                          <div className="active-substances">
                            {drug.activeSubstances.join(', ')}
                          </div>
                        )}
                      </td>
                      <td>{drug.drugIdPK}</td>
                      <td>{drug.licenceNumber}</td>
                      <td>{drug.dosageForm}</td>
                      <td>{drug.marketInfo}</td>
                      <td>
                        <div className="status-badges">
                          {drug.isWithdrawn && <span className="badge badge-withdrawn">Withdrawn</span>}
                          {drug.isInterchangeable && <span className="badge badge-interchangeable">Interchangeable</span>}
                          {!drug.isWithdrawn && <span className="badge badge-active">Active</span>}
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn-link"
                          onClick={() => setSelectedDrug(drug)}
                          aria-label={`View details for ${drug.productName}`}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="pagination" role="navigation" aria-label="Pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <div className="pagination-pages">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      const showPage =
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      
                      if (!showPage) {
                        // Show ellipsis
                        if (page === currentPage - 3 || page === currentPage + 3) {
                          return (
                            <span key={page} className="pagination-ellipsis">
                              ...
                            </span>
                          )
                        }
                        return null
                      }

                      return (
                        <button
                          key={page}
                          className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                          aria-label={`Page ${page}`}
                          aria-current={currentPage === page ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {selectedDrug && (
          <div className="drug-detail-panel">
            <div className="panel-header">
              <h2>Drug Details</h2>
              <button className="btn-close" onClick={() => setSelectedDrug(null)} aria-label="Close details">
                ×
              </button>
            </div>
            <div className="panel-body">
              <div className="detail-section">
                <h3>{selectedDrug.productName}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Drug ID PK</label>
                    <p>{selectedDrug.drugIdPK}</p>
                  </div>
                  <div className="detail-item">
                    <label>Licence Number</label>
                    <p>{selectedDrug.licenceNumber}</p>
                  </div>
                  {selectedDrug.interchangeableListCode && (
                    <div className="detail-item">
                      <label>Interchangeable List Code</label>
                      <p>{selectedDrug.interchangeableListCode}</p>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>PA Holder</label>
                    <p>{selectedDrug.paHolder}</p>
                  </div>
                  <div className="detail-item">
                    <label>Authorised Date</label>
                    <p>{selectedDrug.authorisedDate}</p>
                  </div>
                  <div className="detail-item">
                    <label>Product Type</label>
                    <p>{selectedDrug.productType}</p>
                  </div>
                  <div className="detail-item">
                    <label>Dosage Form</label>
                    <p>{selectedDrug.dosageForm}</p>
                  </div>
                  <div className="detail-item">
                    <label>Market Info</label>
                    <p>{selectedDrug.marketInfo}</p>
                  </div>
                  <div className="detail-item">
                    <label>Registration Status</label>
                    <p>{selectedDrug.registrationStatus}</p>
                  </div>
                  {selectedDrug.atcs.length > 0 && (
                    <div className="detail-item full-width">
                      <label>ATC Codes</label>
                      <p>{selectedDrug.atcs.join(', ')}</p>
                    </div>
                  )}
                  {selectedDrug.activeSubstances.length > 0 && (
                    <div className="detail-item full-width">
                      <label>Active Substances</label>
                      <p>{selectedDrug.activeSubstances.join(', ')}</p>
                    </div>
                  )}
                  {selectedDrug.routesOfAdministration && selectedDrug.routesOfAdministration.length > 0 && (
                    <div className="detail-item full-width">
                      <label>Routes of Administration</label>
                      <p>{selectedDrug.routesOfAdministration.join(', ')}</p>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>Dispensing Legal Status</label>
                    <p>{selectedDrug.dispensingLegalStatus}</p>
                  </div>
                  <div className="detail-item">
                    <label>Supply Legal Status</label>
                    <p>{selectedDrug.supplyLegalStatus}</p>
                  </div>
                  <div className="detail-item">
                    <label>Promotion Legal Status</label>
                    <p>{selectedDrug.promotionLegalStatus}</p>
                  </div>
                  {selectedDrug.legalBasis && (
                    <div className="detail-item full-width">
                      <label>Legal Basis</label>
                      <p>{selectedDrug.legalBasis}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

